import React from 'react';
import { ChevronDownIcon } from 'lucide-react';

/**
 * Campo base de TODOS los formularios públicos de Sistema AVA.
 *
 * Es la única implementación: la usan el editor de formularios, la vista
 * previa del constructor y la landing publicada, en escritorio, tablet y
 * móvil. Cualquier corrección hecha aquí llega automáticamente a todas las
 * landings y formularios, actuales y futuros, sin migrar nada.
 *
 * Geometría del icono interior (la causa del solape anterior):
 *   wrapper  → position: relative
 *   icono    → absoluto a 16 px, centrado vertical, 20 × 20, sin eventos
 *   campo    → padding-left 48 px CON icono · 16 px sin icono
 * Así quedan ~12 px de aire entre el icono y el texto, y el placeholder nunca
 * puede montarse encima.
 */

const LABEL = 'mb-1.5 block text-left text-[13px] font-medium text-foreground sm:text-sm';
const BASE = 'h-12 w-full rounded-xl border bg-background text-left text-[15px] text-foreground transition-colors placeholder:text-[#A7AFBD] focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60';
function borderFor(error?: string) {
  return error ? 'border-red-400 focus:border-red-400' : 'border-border focus:border-primary';
}
interface FieldShellProps {
  id: string;
  label?: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  children: React.ReactNode;
}
function FieldShell({
  id,
  label,
  required,
  error,
  helperText,
  children
}: FieldShellProps) {
  return <div className="w-full" data-id="element-570" data-mp="100tb4h3o8qv1">
      {label && <label htmlFor={id} className={LABEL} data-id="element-571" data-mp="zzrmf9tqg5uu">
          {label}
          {required && <span className="ml-0.5 text-primary" aria-hidden="true" data-id="element-572" data-mp="lzgv65oigp95">
              *
            </span>}
        </label>}
      <div className="relative w-full" data-id="element-573" data-mp="qy1um2vxv4wj">{children}</div>
      {error ? <p id={`${id}-error`} role="alert" className="mt-1.5 text-left text-[13px] font-medium text-red-600" data-id="element-574" data-mp="36q7vlh3uwfxt">
          {error}
        </p> : helperText ? <p className="mt-1.5 text-left text-[13px] text-muted-foreground" data-id="element-575" data-mp="3br5ejvwoxe0d">
          {helperText}
        </p> : null}
    </div>;
}

/** Icono interior. Un único icono por campo, nunca duplicado. */
function FieldIcon({
  icon
}: {
  icon: React.ReactNode;
}) {
  return <span aria-hidden="true" className="pointer-events-none absolute z-[1] flex items-center justify-center text-muted-foreground [&>svg]:h-5 [&>svg]:w-5" style={{
    left: 16,
    top: '50%',
    width: 20,
    height: 20,
    transform: 'translateY(-50%)'
  }} data-id="element-576" data-mp="2iumghmqx88k7">
      {icon}
    </span>;
}
export interface FormFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
}
export function FormField({
  id,
  label,
  error,
  helperText,
  icon,
  required,
  className = '',
  style,
  ...rest
}: FormFieldProps) {
  /* El padding va en estilo en línea, sobre el INPUT real: así ningún utilitario
     ni hoja de estilos posterior puede reducirlo. Icono a 16 px + 20 px de
     ancho + ~18 px de aire = el texto empieza en 54 px. */
  return <FieldShell id={id} label={label} required={required} error={error} helperText={helperText} data-id="element-577" data-mp="nij8f2o80la">
      {icon && <FieldIcon icon={icon} data-id="element-578" data-mp="5md21h0n1p5e" />}
      <input id={id} required={required} aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-error` : undefined} className={`${BASE} ${borderFor(error)} ${className}`} style={{
      paddingLeft: icon ? 54 : 16,
      paddingRight: 16,
      ...style
    }} {...rest} data-id="element-579" data-mp="aewgeg75gob9" />
    </FieldShell>;
}
export interface FormTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  id: string;
  label?: string;
  error?: string;
  helperText?: string;
}
export function FormTextarea({
  id,
  label,
  error,
  helperText,
  required,
  rows = 5,
  className = '',
  ...rest
}: FormTextareaProps) {
  return <FieldShell id={id} label={label} required={required} error={error} helperText={helperText} data-id="element-580" data-mp="133btuyamuykh">
      <textarea id={id} rows={rows} required={required} aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-error` : undefined} className={`${BASE} h-auto px-4 py-3 leading-relaxed ${borderFor(error)} ${className}`} {...rest} data-id="element-581" data-mp="174dqd7d60wir" />
    </FieldShell>;
}
export interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  id: string;
  label?: string;
  error?: string;
  helperText?: string;
  placeholder?: string;
  options: {
    value: string;
    label: string;
  }[];
}

/**
 * Desplegable con estructura propia: el chevron va a la derecha y el texto
 * conserva su padding, por lo que no se le aplica el padding del icono.
 */
export function FormSelect({
  id,
  label,
  error,
  helperText,
  placeholder,
  options,
  required,
  className = '',
  ...rest
}: FormSelectProps) {
  return <FieldShell id={id} label={label} required={required} error={error} helperText={helperText} data-id="element-582" data-mp="1tgghksof1nfn">
      <select id={id} required={required} aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-error` : undefined} className={`${BASE} appearance-none pl-4 pr-9 ${borderFor(error)} ${className}`} {...rest} data-id="element-583" data-mp="27bsk3m8tci74">
        {placeholder && <option value="" disabled data-id="element-584" data-mp="2qzx8jr8d5m16">
            {placeholder}
          </option>}
        {options.map(option => <option key={option.value} value={option.value} data-id="element-585" data-mp="2d0p7essn43uc">
            {option.label}
          </option>)}
      </select>
      <ChevronDownIcon aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" data-id="element-586" data-mp="2hxo0r94hc6rh" />
    </FieldShell>;
}